import { CUSTOM_MESSAGE_GALLERY_NAME_PLACEHOLDER } from '../constants/custom-messages';

const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

/**
 * Turns a 1-based sequence number into its ordinal word ("First", "Second"...).
 * Falls back to a numeric ordinal ("6th") past the fifth.
 * @param {number|string} sequenceNumber 1-based sequence number.
 * @returns {string}
 */
export const getSequenceOrdinal = sequenceNumber => {
  const number = Number(sequenceNumber);

  if (!Number.isInteger(number) || number < 1) {
    return '';
  }

  return ORDINALS[number - 1] ?? `${number}th`;
};

export const getMatchMessageTitle = sequenceNumber =>
  `${getSequenceOrdinal(sequenceNumber).toLowerCase()} match message`;

export const hasGalleryNamePlaceholder = body =>
  (body ?? '').includes(CUSTOM_MESSAGE_GALLERY_NAME_PLACEHOLDER);

/**
 * Inserts a placeholder into a message body. Prepend placeholders (like the
 * greeting) always go at the very top, otherwise the placeholder is spliced in
 * at the caret position or appended when no caret is known. Spliced or
 * appended placeholders get exactly one space on each side: runs of spaces
 * around the caret collapse to one, and a missing one is added (except at the
 * start of the body or of a line).
 *
 * @param {string} body Current message body.
 * @param {string} placeholder Placeholder to insert, e.g. "{galleryName}".
 * @param {object} [options]
 * @param {number} [options.selectionStart] Caret position in the textarea.
 * @param {boolean} [options.prepend] Force the placeholder to the top.
 * @returns {{ body: string, caretPosition: number }}
 */
export const insertPlaceholder = (
  body,
  placeholder,
  { selectionStart, prepend } = {},
) => {
  const currentBody = body ?? '';

  if (prepend) {
    const newBody = `${placeholder}\n\n${currentBody}`;
    return { body: newBody, caretPosition: newBody.length };
  }

  const position =
    Number.isInteger(selectionStart) && selectionStart > 0
      ? selectionStart
      : currentBody.length;
  const before = currentBody.slice(0, position).replace(/[ \t]+$/, '');
  const after = currentBody.slice(position).replace(/^[ \t]+/, '');
  const leadingSpace = before && !/\s$/.test(before) ? ' ' : '';
  const inserted = `${before}${leadingSpace}${placeholder} `;

  return { body: inserted + after, caretPosition: inserted.length };
};

/**
 * Finds the custom message assigned to an album folder for a given sequence number.
 * @param {object} albumFolder Album folder with `custom_messages` loaded.
 * @param {number} [sequenceNumber] 1-based sequence number, defaults to 1.
 * @returns {object|undefined}
 */
export const getAlbumFolderCustomMessage = (albumFolder, sequenceNumber = 1) =>
  (albumFolder?.custom_messages?.records ?? []).find(
    record => record?.custom_message?.sequence_number === sequenceNumber,
  )?.custom_message;
